import type { Language } from '@/types/common'

const aboutTeaImageUrl = '/mock/about/our-products-tea.webp'
const aboutCandleImageUrl = '/mock/about/our-products-candle.webp'
const storyImageUrl = 'https://www.cloutyspace.com/wp-content/uploads/2023/07/Our-Story_1.webp'
const findingBalanceImageUrl = 'https://www.cloutyspace.com/wp-content/uploads/2023/07/Finding-Balance_1.webp'
const importanceOfSelfCareImageUrl = 'https://www.cloutyspace.com/wp-content/uploads/2023/07/The-Importance-of-Self-Care_1.webp'
const teaBagBrewingImageUrl = 'https://www.cloutyspace.com/wp-content/uploads/2023/07/From-Hot-to-Cold_1.webp'
const looseLeafBrewingImageUrl = 'https://www.cloutyspace.com/wp-content/uploads/2023/07/From-Steeping-to-Sipping_1.webp'

const learnArticleBodies = {
  selfCareBalance: `
    <p>In this article, we explore the art of finding balance and practical ways to prioritize self-care, even in the middle of a hectic schedule. Small mindful rituals can help restore harmony in your life and reconnect your mind, body, and daily rhythm.</p>
    <h2>The Importance of Self-Care in a Hectic Schedule</h2>
    <p>Taking care of ourselves often falls behind work, family, and other responsibilities. When that happens for too long, stress and burnout become easier to ignore. Self-care works best when it is treated as regular maintenance instead of a rare reward.</p>
    <h2>Self-Care Practices for Busy Individuals</h2>
    <ul>
      <li>Block dedicated time for yourself, even if it is only a few minutes.</li>
      <li>Use breathing, meditation, or tea rituals to create a quiet pause.</li>
      <li>Move your body in a low-pressure way, such as a short walk or stretch.</li>
      <li>Set boundaries around commitments that drain your energy.</li>
      <li>Make space for hobbies and activities that bring you back to yourself.</li>
    </ul>
    <h2>Mindful Activities You Can Try</h2>
    <p>Painting, handcraft, tea meditation, skincare rituals, cooking, and running can all become mindful activities when you slow down and stay present with the experience.</p>
    <h2>Finding Balance</h2>
    <p>Self-care is personal. Start small, keep what works, and adjust the routine as your life changes. The goal is not perfection; it is a sustainable way to listen to what you need.</p>
  `,
  selfCareImportance: `
    <p>In a fast-paced and demanding world, it is easy to neglect our own well-being. Self-care brings mental health back into the center of daily life and helps us build a more sustainable rhythm.</p>
    <h2>Understanding Self-Care and Its Importance</h2>
    <p>Self-care is more than a trend. It is the practice of intentionally setting aside time and attention for emotional and mental recovery. It can include mindfulness, boundaries, hobbies, rest, and seeking support when needed.</p>
    <h2>The Impact of a Busy World on Mental Health</h2>
    <p>Constant pressure can increase stress, burnout, isolation, sleep disruption, and mental fatigue. Recognizing those signals early makes it easier to respond before exhaustion becomes normal.</p>
    <h2>Creating a Self-Care Plan That Works for You</h2>
    <p>A useful self-care plan reflects your real schedule and preferences. Choose activities you can repeat, not dramatic resets that only work on perfect days.</p>
    <h2>Making Self-Care a Priority</h2>
    <p>Self-care is not selfish. It is a practical way to protect your energy, resilience, and clarity so you can move through life with more steadiness.</p>
  `,
  teaBagBrewing: `
    <p>Tea bags are convenient, consistent, and flexible. With a few simple adjustments, the same tea bag can support hot brewing, iced tea, and cold brew routines.</p>
    <h2>Hot Tea Bag Brewing</h2>
    <p>Start with fresh water and follow the tea type. Green tea prefers slightly cooler water, while black tea can handle hotter water. Steep gently and avoid squeezing the bag, which can make the cup taste bitter.</p>
    <h2>Iced Tea</h2>
    <p>Brew the tea stronger than usual, then pour it over ice. This keeps the flavor clear after the ice begins to melt. Add fruit, herbs, or honey only after the base tea tastes balanced.</p>
    <h2>Cold Brew</h2>
    <p>Place tea bags in cold water and refrigerate for several hours. Cold brewing creates a smoother cup with less bitterness, making it useful for daily hydration or relaxed afternoon tea.</p>
    <h2>Simple Brewing Reminder</h2>
    <p>Good tea does not need to be complicated. Control water, time, and temperature first; then adjust based on the flavor you enjoy.</p>
  `,
  looseLeafBrewing: `
    <p>Loose leaf tea gives the leaves more room to open, creating deeper aroma and a fuller cup. The basics are simple: measure the tea, heat the water, steep, and taste.</p>
    <h2>Choose Your Tea and Teaware</h2>
    <p>Use clean teaware and enough space for the leaves to expand. A basket infuser, teapot, or gaiwan can all work well when the leaves are not packed too tightly.</p>
    <h2>Mind the Water Temperature</h2>
    <p>Green tea usually needs cooler water, oolong sits in the middle, and black tea can use hotter water. If a tea tastes harsh, lower the temperature or shorten the steep.</p>
    <h2>Steeping and Sipping</h2>
    <p>Start with a moderate tea-to-water ratio, then adjust by taste. Many loose leaf teas can be steeped more than once, with each infusion showing a slightly different side of the tea.</p>
    <h2>Build Your Own Rhythm</h2>
    <p>Brewing loose leaf tea is part technique and part attention. Once you understand the basics, the best cup is the one that fits your own pace.</p>
  `,
} as const
export const siteContent = {
  showcase: {
    en: {
      title: '100% Natural Products',
      description: 'Our products are all natural with no additives. All tea products are guaranteed to contain only your tea of choice, with no blend in other types of tea.',
      productNames: {
        'i-loose-leaf': 'Island Ruby Loose Leaf',
        'g-gift-sets': 'Gift Box Set',
        'i-tea-bags': 'Island Ruby Tea Bags',
      },
    },
    zh: {
      title: '100% 天然產品',
      description: '我們的產品都是純天然的，不含任何添加劑。所有茶葉產品都保證只含有您選擇的茶葉，不混合其他類型的茶葉。',
      productNames: {
        'i-loose-leaf': '島嶼紅玉散茶',
        'g-gift-sets': '禮盒套裝',
        'i-tea-bags': '島嶼紅玉茶包',
      },
    },
  },
  brandStory: {
    en: {
      cards: [
        { id: 'story', title: 'Our Story', alt: 'Tea leaf field' },
        { id: 'knowledge', title: 'Tea Knowledge', alt: 'Flat lay arrangement with tea cups and herbs' },
      ],
      heading: 'Our core to your heart',
      body: 'Reconnect to your life and mind. We promise to provide products that only benefit our customers, both physically and mentally.',
    },
    zh: {
      cards: [
        { id: 'story', title: '我們的故事', alt: '茶園景色' },
        { id: 'knowledge', title: '茶葉知識', alt: '茶具與香草平鋪' },
      ],
      heading: '我們的核心直達您的心',
      body: '重新連接您的生活和心靈。我們承諾提供只對客戶有益的產品，無論是身體上還是精神上。',
    },
  },
  about: {
    en: {
      menuLabel: 'About Us',
      sections: [
        { id: 'story', label: 'Our Story', summary: 'Brand story and mission' },
        { id: 'product', label: 'Our Tea', summary: 'Taiwan tea sourcing and product philosophy' },
        { id: 'contact', label: 'Contact Us', summary: 'Email and form' },
        { id: 'faq', label: 'FAQ', summary: 'Product and shipping questions' },
      ],
      story: {
        title: 'Our Story',
        imageUrl: storyImageUrl,
        imageAlt: 'Clouty Space brand story visual',
        paragraphs: [
          'Welcome to Clouty Space, where tea becomes an extraordinary experience. We are a team of passionate tea lovers from Taiwan, driven by the desire to bring you the finest teas straight from the heart of our homeland. But Clouty Space is more than just a tea brand. It is a gateway to a world of flavors and a catalyst for inner peace.',
          'We proudly partner with small tea farms and individual candle studios, joining forces to create products that perfectly align with our mission. Together, we put our hearts and souls into every blend, ensuring that each sip takes you to a place of pure serenity.',
          'Beyond the enchantment of tea, our purpose goes even deeper. We want to empower you with the tools to reconnect with your body and mind, enabling you to live the life you truly desire. Your well-being is our top priority, both physically and mentally. That is why we carefully select only the highest quality products for you, because we believe you deserve nothing less.',
          'Furthermore, we strongly advocate for mental health. We understand the importance of nurturing your mind, and we are here to guide you on your journey of self-discovery through practices like meditation and self-care. At Clouty Space, we are committed to helping you find harmony within yourself every step of the way.',
          'So, come join us on this transformative journey toward a balanced and fulfilled life. Let us embark together, as we embrace the beauty of tea and venture into a realm that knows no boundaries, enriching your senses and nourishing your soul. At Clouty Space, we are more than just a brand. We are your companions on this remarkable quest for tranquility.',
        ],
        signature: 'Clouty Space',
      },
      product: {
        title: 'Our Tea',
        sections: [
          {
            id: 'tea',
            title: 'Our Tea',
            imageUrl: aboutTeaImageUrl,
            imageAlt: 'Tea leaves in a Taiwan tea field',
            blocks: [
              {
                title: 'Taste of Taiwan.',
                paragraphs: [
                  'Taiwan is known for its long-standing history of producing tea. For centuries, this island has been renowned for producing one of the best teas in the world due to its warm climate and abundant rainfall. Taiwan has been cultivating and breeding different types of tea in terms of taste, where you find nowhere else.',
                  'As a kid nurtured by this land, we would like to share our quality and unique tea with the world and, together, introduce our tea culture to you.',
                ],
              },
              {
                title: '100% direct source from Taiwanese farmers.',
                paragraphs: [
                  'We love to explore good tea in Taiwan and bring it to your table. We search around Taiwan to find the best quality tea and buy directly from the farm. This way, we can ensure the quality for you and create the best trading situation for the farmers as well.',
                  'In our own experience, we believe it is quite hard to find a trustable source to buy your own tea in the pool of thousands of options online. We want to create a space where our customers can trust us with our promise of quality. Clouty Space will only provide tea products that source directly from the farm, and we do not mix our tea with cheaper tea. Your teacup and belly are safe with us.',
                ],
              },
            ],
          },
          {
            id: 'candle',
            title: 'Our Candle',
            imageUrl: aboutCandleImageUrl,
            imageAlt: 'Clouty Space candle collection',
            blocks: [
              {
                title: 'Recharging and refreshing',
                paragraphs: [
                  'Lighting up candles is like building up a campfire to warm you up in a chilling forest. We want to create candles that are different in the market and that are suitable for the times when we feel the need to recharge.',
                  'After a long search, we finally found a small candle studio that shares the same value as us. We collaborate with the studio to customize each batch of our candle productions, and the candles are guaranteed to all be handmade with 100% pure palm wax and certified scent oil.',
                  'We hope the love and warmth we put into the candle can also spread into your heart and bring you energy.',
                ],
              },
            ],
          },
        ],
      },
      contact: {
        title: 'Contact us',
        intro: 'We\'d love to hear from you :) Tell us how you feel about our products or any support you need from us. We are happy to help!',
        address: '',
        email: 'service@cloutyspace.com',
        form: {
          namePlaceholder: 'Name',
          emailPlaceholder: 'E-mail',
          messagePlaceholder: 'Message...',
          submitLabel: 'Send',
        },
      },
      faq: {
        title: 'FAQ',
        groups: [
          {
            title: 'Our Tea',
            items: [
              {
                question: '1. Where does Clouty Space source your tea?',
                answers: [
                  'All our tea products are sourced directly from Taiwan, the birthplace of our founder. Our mission is to introduce you to the exquisite quality of Taiwanese tea.',
                  'We take great pride in highlighting Taiwan as one of the best regions renowned for tea production.',
                ],
              },
              {
                question: '2. How should I store my tea?',
                answers: [
                  'Store your tea in a cool and dry place away from direct sunlight.',
                  'If possible, keep your tea in an airtight container, but do not vacuum-pack black tea because it can fracture the leaves.',
                ],
              },
              {
                question: '3. Is your tea organic?',
                answers: [
                  'No, our current tea collection is not certified organic, but we work with trusted suppliers and growers who follow strict quality standards.',
                  'All our tea has passed the SGS pesticide residue standards in Taiwan, and we are working toward offering trustworthy organic options in the future.',
                ],
              },
              {
                question: '4. Does your tea contain caffeine?',
                answers: [
                  'Yes. All tea contains caffeine, though a brewed cup of tea usually has about one-third to one-half of the caffeine found in coffee.',
                  'Green tea and roasted tea generally contain less caffeine than oolong or black tea, but levels still vary by cultivar, growing conditions, and brewing method.',
                ],
              },
              {
                question: '5. What\'s the difference between black tea, oolong tea, and green tea?',
                answers: [
                  'All teas come from the same plant, Camellia sinensis. The difference comes from the level of oxidation during processing.',
                  'Black tea is fully oxidized, oolong is partially oxidized, and green tea is unoxidized, which creates different aromas, flavors, and antioxidant profiles.',
                ],
              },
              {
                question: '6. Why is Taiwan the best origin of Oolong tea?',
                answers: [
                  'Taiwan is renowned for high-quality Oolong tea because of its unique climate, soil conditions, and long history of careful cultivation and processing.',
                  'That combination creates smooth and complex flavors that are difficult to find elsewhere.',
                ],
              },
              {
                question: '7. Why are some of your tea products not available all year round?',
                answers: [
                  'We source from smaller tea farms with limited production and quality-first practices, and seasonal weather changes can affect flavor from batch to batch.',
                  'To protect quality, we only purchase limited quantities, so some teas are not always available throughout the year.',
                ],
              },
              {
                question: '8. Where can I find Clouty Space tea?',
                answers: [
                  'Clouty Space tea is currently available on Amazon and on our website. Limited gift box editions are launched on the website only.',
                  'If you are purchasing outside the USA and cannot order through Amazon, you can contact us directly and place the order through the website.',
                ],
              },
            ],
          },
          {
            title: 'Shipping & Return',
            items: [
              {
                question: '1. When will my order be shipped and when can I receive it?',
                answers: [
                  'We currently ship within the USA and Taiwan. Orders in the USA usually arrive within 3 to 5 days, while gift boxes may take around 10 to 14 working days.',
                  'Gift boxes ship directly from Taiwan after payment is processed, and customer service will provide the shipping date and tracking number.',
                ],
              },
              {
                question: '2. Can I return my products?',
                answers: [
                  'If the product you received is incorrect or below expected quality, please contact customer service within 30 days of your order date.',
                  'We do not encourage returns based only on personal taste preferences, but we are happy to help if something went wrong.',
                ],
              },
              {
                question: '3. Something is wrong with my order. What should I do?',
                answers: [
                  'If you purchased through Amazon, contact us on Amazon for exchanges or refunds.',
                  'If you purchased through our website, email our customer service team for help with exchanges or refunds for damaged or incorrect orders.',
                ],
              },
              {
                question: '4. I can\'t find any solutions here. What can I do?',
                answers: [
                  'If you still have questions, email us at service@cloutyspace.com. We are here to help and keep the experience respectful and low-pressure.',
                ],
              },
            ],
          },
        ],
      },
    },
    zh: {
      menuLabel: '關於我們',
      sections: [
        { id: 'story', label: '我們的故事', summary: '品牌故事與使命' },
        { id: 'product', label: '我們的茶', summary: '台灣茶來源與產品理念' },
        { id: 'contact', label: '聯絡我們', summary: '信箱與聯絡表單' },
        { id: 'faq', label: '常見問題', summary: '商品與寄送問題' },
      ],
      story: {
        title: '我們的故事',
        imageUrl: storyImageUrl,
        imageAlt: 'Clouty Space 品牌故事視覺',
        paragraphs: [
          '歡迎來到 Clouty Space，讓茶成為一段更不尋常的體驗。我們是一群來自台灣、熱愛茶的團隊，希望把來自家鄉的好茶直接帶到你面前。對我們來說，Clouty Space 不只是茶品牌，更是一個通往風味世界與內在平靜的入口。',
          '我們與小型茶園及獨立香氛工作室合作，攜手做出真正符合品牌理念的產品。我們把心力放進每一次調配，只希望每一口、每一次點燃，都能把你帶往更安穩的狀態。',
          '除了茶本身的迷人之處，我們的目的更深一層。我們希望提供重新連結身體與心靈的工具，讓你更靠近自己真正想過的生活。你的身心狀態始終是我們最在意的事，因此我們只挑選高品質的產品，因為我們相信你值得更好的選擇。',
          '我們也非常重視心理健康。我們理解照顧內在的重要性，並希望陪伴你透過冥想、自我照顧等方式展開自我探索。Clouty Space 會在這條路上持續陪你，一步一步找回內在的平衡。',
          '歡迎加入這段走向平衡與充實生活的旅程。讓我們一起擁抱茶的美好，走進一個沒有邊界的感官世界，重新滋養你的心與日常。對 Clouty Space 而言，我們不只是品牌，更是你通往安定生活的同行者。',
        ],
        signature: 'Clouty Space',
      },
      product: {
        title: '我們的茶',
        sections: [
          {
            id: 'tea',
            title: '我們的茶',
            imageUrl: aboutTeaImageUrl,
            imageAlt: '台灣茶園茶葉',
            blocks: [
              {
                title: '來自台灣的風味。',
                paragraphs: [
                  '台灣擁有悠久的製茶歷史。幾個世紀以來，這座島嶼因為溫暖氣候與豐沛雨量，孕育出世界知名的優質茶葉，也發展出在其他地方難以取代的風味樣貌。',
                  '我們在這片土地長大，也希望把屬於台灣的品質與風味分享給世界，讓更多人藉由 Clouty Space 接觸台灣茶文化。',
                ],
              },
              {
                title: '100% 直接來自台灣茶農。',
                paragraphs: [
                  '我們喜歡在台灣尋找好茶，並把它帶到你的餐桌上。我們走訪不同產地，直接向茶農採購，這樣既能確保品質，也能讓生產者獲得更合理的回報。',
                  '以我們自己的經驗來說，線上選茶非常容易迷失在大量選項裡。因此我們想打造一個你可以放心信任的空間。Clouty Space 只提供直接來自產地的茶品，不混入廉價茶葉，你的每一杯茶都能喝得安心。',
                ],
              },
            ],
          },
          {
            id: 'candle',
            title: '我們的香氛蠟燭',
            imageUrl: aboutCandleImageUrl,
            imageAlt: 'Clouty Space 香氛產品',
            blocks: [
              {
                title: '重新充電，也重新清醒。',
                paragraphs: [
                  '點起蠟燭，就像在寒冷森林裡點起營火。我們想做出不同於市場主流、真正適合人在需要重新整理自己時使用的香氛產品。',
                  '經過長時間尋找，我們終於找到一間與我們價值觀一致的小型工作室，並共同客製每一批蠟燭。所有產品皆為手工製作，使用 100% 純棕櫚蠟與合格香氛油。',
                  '我們希望這份溫度與心意，也能透過香氣傳遞到你心裡，為你的生活補充能量。',
                ],
              },
            ],
          },
        ],
      },
      contact: {
        title: '聯絡我們',
        intro: '我們很想聽聽你的想法 :) 無論是對產品的感受、合作機會，或任何需要協助的地方，都歡迎聯絡我們。',
        address: '',
        email: 'service@cloutyspace.com',
        form: {
          namePlaceholder: 'Name',
          emailPlaceholder: 'E-mail',
          messagePlaceholder: 'Message...',
          submitLabel: 'Send',
        },
      },
      faq: {
        title: '常見問題',
        groups: [
          {
            title: '商品相關',
            items: [
              {
                question: '1. Clouty Space 的茶從哪裡來？',
                answers: [
                  '我們所有茶品都直接來自台灣，也就是品牌創辦人的家鄉。我們希望把台灣茶的細緻品質介紹給更多人。',
                  '台灣是世界上極具代表性的茶葉產地之一，這也是我們最想強調的來源價值。',
                ],
              },
              {
                question: '2. 茶葉應該怎麼保存？',
                answers: [
                  '最重要的是放在陰涼乾燥處，避免陽光直射。',
                  '若可以，建議放進密封容器保存，但黑茶不要真空包裝，以免茶葉破碎。',
                ],
              },
              {
                question: '3. 你們的茶是有機的嗎？',
                answers: [
                  '目前販售中的茶品尚未取得有機認證，但我們與可信任的供應商合作，並遵循嚴格的品質標準。',
                  '所有茶品皆通過台灣 SGS 農藥殘留檢測，同時我們也持續尋找值得信任的有機茶來源。',
                ],
              },
              {
                question: '4. 茶裡有咖啡因嗎？',
                answers: [
                  '有，所有茶類都含有咖啡因，不過一般來說一杯茶的咖啡因含量約為咖啡的三分之一到二分之一。',
                  '綠茶與焙火茶通常較低，烏龍與紅茶則相對高一些，但仍會受到品種、生長條件與沖泡方式影響。',
                ],
              },
              {
                question: '5. 紅茶、烏龍茶、綠茶有什麼差別？',
                answers: [
                  '它們都來自同一種茶樹 Camellia sinensis，差異主要來自製程中的氧化程度。',
                  '紅茶是全氧化、烏龍是部分氧化、綠茶則是不氧化，因此呈現出截然不同的香氣、風味與口感。',
                ],
              },
              {
                question: '6. 為什麼台灣是烏龍茶的重要產地？',
                answers: [
                  '台灣以高品質烏龍茶聞名，原因來自獨特的氣候、土壤條件，以及長期累積的製茶經驗。',
                  '這些條件共同造就出層次細膩、口感圓潤的茶風味，也是其他產區難以複製的地方。',
                ],
              },
              {
                question: '7. 為什麼有些茶不是全年供應？',
                answers: [
                  '我們與小型茶園合作，產量有限，且每一季氣候都可能微幅改變茶的風味。',
                  '為了維持品質，我們只採購有限批次，因此部分茶款不一定全年供應。',
                ],
              },
              {
                question: '8. 我可以在哪裡買到 Clouty Space 的茶？',
                answers: [
                  '目前可在 Amazon 與品牌官網購買；部分限定禮盒只會在官網販售。',
                  '如果你不在美國、無法透過 Amazon 下單，也可以直接透過官網與我們聯繫。',
                ],
              },
            ],
          },
          {
            title: '寄送與退換貨',
            items: [
              {
                question: '1. 訂單何時出貨？多久會收到？',
                answers: [
                  '目前我們提供美國與台灣地區出貨。美國地區一般約 3 到 5 天送達，禮盒產品約需 10 到 14 個工作天。',
                  '禮盒會由台灣直接出貨，付款完成後客服會再提供預估出貨時間與追蹤資訊。',
                ],
              },
              {
                question: '2. 可以退貨嗎？',
                answers: [
                  '若收到的產品與訂單不符，或品質與預期差距過大，請在下單日起 30 天內聯繫客服。',
                  '如果只是個人口味偏好，我們不鼓勵退貨，但若有其他狀況，我們仍會協助處理。',
                ],
              },
              {
                question: '3. 我的訂單有問題，該怎麼辦？',
                answers: [
                  '若你是在 Amazon 購買，請直接透過 Amazon 聯繫我們處理換貨或退款。',
                  '若是在官網購買，請來信客服，我們會協助處理錯誤或損壞商品的退換事宜。',
                ],
              },
              {
                question: '4. 還是找不到答案怎麼辦？',
                answers: [
                  '直接來信 service@cloutyspace.com 即可，我們會用不打擾、低壓力的方式協助你找到答案。',
                ],
              },
            ],
          },
        ],
      },
    },
  },
  learn: {
    en: {
      menuLabel: 'Learn',
      sections: [
        {
          id: 'tea-knowledge',
          label: 'Tea Knowledge',
          summary: 'Brewing guides and tea basics',
        },
        {
          id: 'meditation',
          label: 'Meditation',
          summary: 'Mindful balance and self-care',
        },
        {
          id: 'life',
          label: 'Life',
          summary: 'Daily wellness reflections',
        },
      ],
      searchPlaceholder: 'Search',
      recentPostsTitle: 'Recent Posts',
      categoriesTitle: 'Categories',
      readMoreLabel: 'Read More',
      teaKnowledge: {
        title: 'Tea Knowledge',
        breadcrumbs: ['Home', 'Blog', 'Tea Knowledge'],
        posts: [
          {
            id: 'tea-bag-brewing-guides',
            title: 'From Hot to Cold: Simple Tea Bag Brewing Guides',
            excerpt: 'Tea is one of the most loved beverages worldwide. While some prefer hot tea, others enjoy it cold. But did you know that there are different ways to brew tea? One popular method is using tea bags for quick and flexible daily brewing.',
            contentHtml: learnArticleBodies.teaBagBrewing,
            href: 'https://www.cloutyspace.com/tea-bag-brewing-guides/',
            publishedAt: '2023-09-21',
            categories: ['Tea Knowledge'],
            imageUrl: teaBagBrewingImageUrl,
            imageAlt: 'Tea bag brewing guide visual',
          },
          {
            id: 'guide-to-brewing-loose-leaf-tea',
            title: 'From Steeping to Sipping: A Step-by-Step Guide to Brewing Loose Leaf Tea',
            excerpt: 'There is nothing quite like a freshly brewed cup of loose leaf tea. The aromas, the flavors, and the overall experience feel more immersive than a tea bag, especially when you learn the basics of timing, temperature, and tea-to-water ratio.',
            contentHtml: learnArticleBodies.looseLeafBrewing,
            href: 'https://www.cloutyspace.com/guide-to-brewing-loose-leaf-tea/',
            publishedAt: '2023-09-18',
            categories: ['Tea Knowledge'],
            imageUrl: looseLeafBrewingImageUrl,
            imageAlt: 'Loose leaf tea brewing guide visual',
          },
        ],
      },
      meditation: {
        title: 'Meditation',
        breadcrumbs: ['Home', 'Blog', 'Meditation'],
        posts: [
          {
            id: 'how-to-practice-self-care',
            title: 'Finding Balance: How to Practice Self-Care in a Hectic Schedule and Mindful Activities Suggestions',
            excerpt: 'In this article, we explore the art of finding balance and practical ways to prioritize self-care, even in the middle of a hectic schedule. From simple mindful rituals to low-pressure resets, the goal is to create space for your own recovery.',
            contentHtml: learnArticleBodies.selfCareBalance,
            href: 'https://www.cloutyspace.com/how-to-practice-self-care/',
            publishedAt: '2023-09-28',
            categories: ['Meditation'],
            imageUrl: findingBalanceImageUrl,
            imageAlt: 'Finding balance self-care visual',
          },
          {
            id: 'the-importance-of-self-care',
            title: 'The Importance of Self-Care: Prioritizing Your Mental Health in a Busy World',
            excerpt: 'In today\'s fast-paced world, it is easy to get caught up in the hustle of daily life and neglect our own well-being. This piece reframes self-care as a necessary practice for mental clarity, resilience, and a more sustainable rhythm.',
            contentHtml: learnArticleBodies.selfCareImportance,
            href: 'https://www.cloutyspace.com/the-importance-of-self-care/',
            publishedAt: '2023-09-24',
            categories: ['Meditation', 'Life'],
            imageUrl: importanceOfSelfCareImageUrl,
            imageAlt: 'A woman resting on a sofa at night',
          },
        ],
      },
      life: {
        title: 'Life',
        breadcrumbs: ['Home', 'Blog', 'Life'],
        posts: [
          {
            id: 'the-importance-of-self-care',
            title: 'The Importance of Self-Care: Prioritizing Your Mental Health in a Busy World',
            excerpt: 'In today\'s fast-paced and demanding world, it\'s easy to get caught up in the hustle and bustle of daily life, often neglecting our own well-being in the process. This article brings self-care back into the center of ordinary living.',
            contentHtml: learnArticleBodies.selfCareImportance,
            href: 'https://www.cloutyspace.com/the-importance-of-self-care/',
            publishedAt: '2023-09-24',
            categories: ['Meditation', 'Life'],
            imageUrl: importanceOfSelfCareImageUrl,
            imageAlt: 'A woman resting on a sofa at night',
          },
        ],
      },
      recentPosts: [
        {
          title: 'Finding Balance: How to Practice Self-Care in a Hectic Schedule and Mindful Activities Suggestions',
          href: 'https://www.cloutyspace.com/how-to-practice-self-care/',
          publishedAt: '2023-09-28',
        },
        {
          title: 'The Importance of Self-Care: Prioritizing Your Mental Health in a Busy World',
          href: 'https://www.cloutyspace.com/the-importance-of-self-care/',
          publishedAt: '2023-09-24',
        },
        {
          title: 'From Hot to Cold: Simple Tea Bag Brewing Guides',
          href: 'https://www.cloutyspace.com/tea-bag-brewing-guides/',
          publishedAt: '2023-09-21',
        },
        {
          title: 'From Steeping to Sipping: A Step-by-Step Guide to Brewing Loose Leaf Tea',
          href: 'https://www.cloutyspace.com/guide-to-brewing-loose-leaf-tea/',
          publishedAt: '2023-09-18',
        },
      ],
    },
    zh: {
      menuLabel: 'Learn',
      sections: [
        {
          id: 'tea-knowledge',
          label: '茶葉知識',
          summary: '沖泡指南與茶葉基礎',
        },
        {
          id: 'meditation',
          label: '冥想',
          summary: '正念平衡與自我照顧',
        },
        {
          id: 'life',
          label: '生活',
          summary: '日常療癒與生活節奏',
        },
      ],
      searchPlaceholder: 'Search',
      recentPostsTitle: 'Recent Posts',
      categoriesTitle: 'Categories',
      readMoreLabel: 'Read More',
      teaKnowledge: {
        title: '茶葉知識',
        breadcrumbs: ['Home', 'Blog', 'Tea Knowledge'],
        posts: [
          {
            id: 'tea-bag-brewing-guides',
            title: '從熱泡到冷泡：簡單茶包沖泡指南',
            excerpt: '茶是全球最受歡迎的飲品之一，有些人喜歡熱茶，也有人偏愛冷泡。這篇文章把舊站的茶包沖泡內容整理成更容易閱讀的入門摘要。',
            contentHtml: learnArticleBodies.teaBagBrewing,
            href: 'https://www.cloutyspace.com/tea-bag-brewing-guides/',
            publishedAt: '2023-09-21',
            categories: ['Tea Knowledge'],
            imageUrl: teaBagBrewingImageUrl,
            imageAlt: '茶包沖泡指南視覺',
          },
          {
            id: 'guide-to-brewing-loose-leaf-tea',
            title: '從浸泡到品飲：散茶沖泡逐步指南',
            excerpt: '一杯剛泡好的散茶，從香氣到口感都比茶包更有層次。這篇內容把散茶的沖泡時間、溫度與茶水比例，用清楚的步驟整理出來。',
            contentHtml: learnArticleBodies.looseLeafBrewing,
            href: 'https://www.cloutyspace.com/guide-to-brewing-loose-leaf-tea/',
            publishedAt: '2023-09-18',
            categories: ['Tea Knowledge'],
            imageUrl: looseLeafBrewingImageUrl,
            imageAlt: '散茶沖泡指南視覺',
          },
        ],
      },
      meditation: {
        title: '冥想',
        breadcrumbs: ['Home', 'Blog', 'Meditation'],
        posts: [
          {
            id: 'how-to-practice-self-care',
            title: '找回平衡：忙碌生活中的自我照顧與正念練習建議',
            excerpt: '這篇文章整理舊站的自我照顧主題，聚焦在如何於忙碌生活中練習留白、建立正念節奏，並逐步把照顧自己變成可持續的習慣。',
            contentHtml: learnArticleBodies.selfCareBalance,
            href: 'https://www.cloutyspace.com/how-to-practice-self-care/',
            publishedAt: '2023-09-28',
            categories: ['Meditation'],
            imageUrl: findingBalanceImageUrl,
            imageAlt: '自我照顧與找回平衡視覺',
          },
          {
            id: 'the-importance-of-self-care',
            title: '自我照顧的重要性：在忙碌世界裡優先照顧心理健康',
            excerpt: '在節奏快速、要求密集的生活裡，人很容易忽略自己的身心狀態。這篇文章把自我照顧重新放回日常核心，提醒我們它不是奢侈，而是必要。',
            contentHtml: learnArticleBodies.selfCareImportance,
            href: 'https://www.cloutyspace.com/the-importance-of-self-care/',
            publishedAt: '2023-09-24',
            categories: ['Meditation', 'Life'],
            imageUrl: importanceOfSelfCareImageUrl,
            imageAlt: '夜晚坐在沙發上的女子',
          },
        ],
      },
      life: {
        title: '生活',
        breadcrumbs: ['Home', 'Blog', 'Life'],
        posts: [
          {
            id: 'the-importance-of-self-care',
            title: '自我照顧的重要性：在忙碌世界裡優先照顧心理健康',
            excerpt: '舊站的 Life 分類以心理健康與日常平衡為核心。這篇文章聚焦在如何在繁忙生活裡重新看待休息、界線與對自己的照顧。',
            contentHtml: learnArticleBodies.selfCareImportance,
            href: 'https://www.cloutyspace.com/the-importance-of-self-care/',
            publishedAt: '2023-09-24',
            categories: ['Meditation', 'Life'],
            imageUrl: importanceOfSelfCareImageUrl,
            imageAlt: '夜晚坐在沙發上的女子',
          },
        ],
      },
      recentPosts: [
        {
          title: '找回平衡：忙碌生活中的自我照顧與正念練習建議',
          href: 'https://www.cloutyspace.com/how-to-practice-self-care/',
          publishedAt: '2023-09-28',
        },
        {
          title: '自我照顧的重要性：在忙碌世界裡優先照顧心理健康',
          href: 'https://www.cloutyspace.com/the-importance-of-self-care/',
          publishedAt: '2023-09-24',
        },
        {
          title: '從熱泡到冷泡：簡單茶包沖泡指南',
          href: 'https://www.cloutyspace.com/tea-bag-brewing-guides/',
          publishedAt: '2023-09-21',
        },
        {
          title: '從浸泡到品飲：散茶沖泡逐步指南',
          href: 'https://www.cloutyspace.com/guide-to-brewing-loose-leaf-tea/',
          publishedAt: '2023-09-18',
        },
      ],
    },
  },
  seo: {
    en: {
      home: {
        title: 'CloutySpace | Taiwan Tea, Gift Sets, and Wellness Rituals',
        description: 'Discover CloutySpace tea collections, curated gift sets, and Taiwan tea stories designed for calm daily rituals.',
      },
      shop: {
        title: 'Shop Tea Collections | CloutySpace',
        description: 'Browse loose leaf teas, tea bags, and curated gift sets from CloutySpace with filters for format and tea style.',
      },
      about: {
        title: 'About CloutySpace | Taiwan Tea Brand Story',
        description: 'Learn how CloutySpace brings Taiwan tea culture, sustainability, and mindful rituals into a modern brand experience.',
      },
      learn: {
        title: 'Tea Learning Hub | CloutySpace',
        description: 'Read brewing guides, tea knowledge, and Taiwan tea culture content curated for curious tea drinkers.',
      },
      productFallback: {
        title: 'Tea Product Details | CloutySpace',
        description: 'View CloutySpace tea product details, tasting notes, and shopping information.',
      },
    },
    zh: {
      home: {
        title: 'CloutySpace | 台灣茶、禮盒與日常療癒儀式',
        description: '探索 CloutySpace 的台灣茶系列、禮盒組與品牌故事，為日常生活打造更平衡的療癒節奏。',
      },
      shop: {
        title: '商品總覽 | CloutySpace',
        description: '瀏覽 CloutySpace 的散茶、茶包與禮盒商品，依照茶種與包裝形式快速篩選適合的選擇。',
      },
      about: {
        title: '關於 CloutySpace | 品牌故事與理念',
        description: '了解 CloutySpace 如何以台灣茶文化、永續理念與身心平衡為核心，打造現代茶品牌體驗。',
      },
      learn: {
        title: '茶知識專區 | CloutySpace',
        description: '閱讀 CloutySpace 精選的沖泡指南、茶葉知識與台灣茶文化內容。',
      },
      productFallback: {
        title: '商品詳情 | CloutySpace',
        description: '查看 CloutySpace 商品資訊、風味描述與購買細節。',
      },
    },
  },
} as const

export const getShowcaseContent = (lang: Language) => siteContent.showcase[lang]

export const getBrandStoryContent = (lang: Language) => siteContent.brandStory[lang]

export const getAboutContent = (lang: Language) => siteContent.about[lang]

export const getLearnContent = (lang: Language) => siteContent.learn[lang]

export const getSeoContent = (lang: Language) => siteContent.seo[lang]
